import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Save, ArrowLeft, Check, Users as UsersIcon, Bot } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { CHARACTERS, HUMAN_CHARACTERS, CREATURE_CHARACTERS, getCharacter } from "@/lib/characters";
import { defaultAvatar } from "@/lib/avatar";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [selectedId, setSelectedId] = useState(() => {
    return user?.avatar?.character_id || defaultAvatar(user?.user_id).character_id;
  });
  const [filter, setFilter] = useState("all"); // all | human | creature
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.avatar?.character_id) setSelectedId(user.avatar.character_id);
  }, [user]);

  if (!user) return null;

  const selected = getCharacter(selectedId);
  const list = filter === "human" ? HUMAN_CHARACTERS : filter === "creature" ? CREATURE_CHARACTERS : CHARACTERS;

  const onSave = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/avatar", { character_id: selectedId });
      await refresh();
      toast.success(`Avatar set to ${selected.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] relative" data-testid="profile-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/" className="btn-ghost !pl-2" data-testid="profile-back">
          <ArrowLeft className="w-4 h-4" /> Home
        </Link>

        <div className="mt-6 grid lg:grid-cols-12 gap-8 lg:gap-12">
          {/* PREVIEW (sticky) */}
          <div className="lg:col-span-5 xl:col-span-4">
            <div className="lg:sticky lg:top-24 space-y-4">
              <motion.div
                key={selected.id}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35 }}
                className="relative aspect-[4/5] rounded-3xl overflow-hidden holo-frame"
                style={{ background: `linear-gradient(180deg, #${selected.accent}33, #0D0A1C 80%)` }}
              >
                <img src={selected.img} alt={selected.name} className="w-full h-full object-cover" />
                <div className="absolute top-4 left-4 z-10 flex flex-col gap-1.5">
                  <span className="hud-chip">UNIT_{selected.id.toUpperCase()}</span>
                  <span className="hud-chip" style={{ color: `#${selected.accent}`, borderColor: `#${selected.accent}80`, background: `#${selected.accent}22` }}>
                    {selected.role.toUpperCase()}
                  </span>
                  <span className="hud-chip" style={{ color: selected.type === "creature" ? "#FF003C" : "#39FF14", borderColor: selected.type === "creature" ? "rgba(255,0,60,0.5)" : "rgba(57,255,20,0.5)", background: selected.type === "creature" ? "rgba(255,0,60,0.12)" : "rgba(57,255,20,0.12)" }}>
                    {selected.type === "creature" ? "XENO" : "HUMAN"}
                  </span>
                </div>
                <div className="absolute bottom-0 inset-x-0 p-5 bg-gradient-to-t from-ink-900 via-ink-900/85 to-transparent z-10">
                  <div className="label-mono text-brand-cyan">// signature</div>
                  <div className="font-display font-extrabold text-3xl text-white tracking-tight mt-1">{selected.name}</div>
                  <div className="text-sm font-mono text-white/70 mt-1">{selected.tag}</div>
                </div>
              </motion.div>
              <button
                onClick={onSave}
                disabled={saving}
                className="btn-primary w-full"
                data-testid="avatar-save-btn"
              >
                <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save character"}
              </button>
            </div>
          </div>

          {/* PICKER */}
          <div className="lg:col-span-7 xl:col-span-8">
            <header className="mb-6">
              <div className="label-mono text-brand-cyan">// choose your form</div>
              <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter mt-2">
                Character Roster
              </h1>
              <p className="text-white/60 mt-2">Pick a cyberpunk human or otherworldly creature. Each one has their own role and signature.</p>
            </header>

            {/* Type filter */}
            <div className="flex gap-2 mb-6" data-testid="character-filter">
              <FilterButton active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all">
                <UsersIcon className="w-3.5 h-3.5" /> All <span className="opacity-50">({CHARACTERS.length})</span>
              </FilterButton>
              <FilterButton active={filter === "human"} onClick={() => setFilter("human")} testid="filter-human">
                <UsersIcon className="w-3.5 h-3.5" /> Humans <span className="opacity-50">({HUMAN_CHARACTERS.length})</span>
              </FilterButton>
              <FilterButton active={filter === "creature"} onClick={() => setFilter("creature")} testid="filter-creature">
                <Bot className="w-3.5 h-3.5" /> Creatures <span className="opacity-50">({CREATURE_CHARACTERS.length})</span>
              </FilterButton>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {list.map((c) => {
                const active = c.id === selectedId;
                return (
                  <button
                    key={c.id}
                    onClick={() => setSelectedId(c.id)}
                    className={`relative aspect-[3/4] rounded-2xl overflow-hidden border-2 transition-all group ${
                      active
                        ? "border-brand-magenta scale-[1.03] shadow-glow-magenta"
                        : "border-white/10 hover:border-white/30"
                    }`}
                    data-testid={`character-${c.id}`}
                  >
                    <img src={c.img} alt={c.name} className="w-full h-full object-cover transition-transform group-hover:scale-105" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/30 to-transparent" />
                    <div className="absolute top-2 left-2 z-10">
                      <span className="hud-chip !text-[8px]" style={{ color: c.type === "creature" ? "#FF003C" : "#39FF14", borderColor: c.type === "creature" ? "rgba(255,0,60,0.5)" : "rgba(57,255,20,0.5)", background: c.type === "creature" ? "rgba(255,0,60,0.12)" : "rgba(57,255,20,0.12)" }}>
                        {c.type === "creature" ? "XENO" : "HUMAN"}
                      </span>
                    </div>
                    {active && (
                      <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-brand-magenta grid place-items-center z-10 shadow-glow-magenta">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div className="absolute bottom-0 inset-x-0 p-2.5 z-10 text-left">
                      <div className="font-display font-bold text-sm text-white truncate">{c.name}</div>
                      <div className="text-[10px] font-mono text-white/60 truncate">{c.role}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterButton({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full font-display font-semibold text-sm transition-all ${
        active
          ? "bg-brand-magenta text-white shadow-glow-magenta"
          : "bg-white/5 text-white/60 border border-white/10 hover:border-white/30 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}
